import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText, Home, Sparkles, Upload } from "lucide-react";
import { useChainStore } from "../data/chainStore";
import { initialsOf } from "../components/contacts/AddContactModal";
import { ChainFlowLogo } from "../components/brand/ChainFlowLogo";

const STAGE_LABEL = { instruction: "Instruction received", offer: "Offer accepted", memo: "Memorandum issued", searches: "Searches in progress", enquiries: "Enquiries with solicitors", mortgage: "Mortgage approved", exchange: "Exchange ready", completion: "Completion booked" };

const PortalLayout = ({ kind, deal, property, agent, partner, partnerLabel, children }) => {
  const nav = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #FAF7FF 0%, #F8FAFC 50%)" }}>
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-600 hover:text-slate-900" data-testid={`${kind}-back`}>
            <ArrowLeft size={13} /> Back
          </button>
          <ChainFlowLogo variant="full" size={26} testid={`${kind}-portal-brand`} />
        </div>
      </header>

      {/* Welcome banner */}
      <section className="max-w-[1100px] mx-auto px-5 md:px-8 pt-8">
        <div className="rounded-3xl overflow-hidden text-white p-6 md:p-8 relative" style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #4F46E5 60%, #4338CA 100%)" }} data-testid={`${kind}-welcome`}>
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-400/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />
          <div className="relative flex items-center gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white flex items-center justify-center cf-shadow-md text-[16px] font-bold">
              {initialsOf(partner?.name || "·")}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-indigo-200 font-semibold mb-1">{partnerLabel} portal · {property?.address?.split(",")[0]}</p>
              <h1 className="font-display text-[24px] md:text-[28px] font-bold leading-tight">Welcome, {partner?.name?.split(" ")[0] || "there"}.</h1>
              <p className="text-[13px] text-indigo-100/85 mt-2 max-w-xl">{kind === "seller" ? "Track every step of your sale, approve documents and stay in sync with your team." : "Track every step of your purchase, upload documents and stay reassured."}</p>
            </div>
            <div className="text-right">
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-indigo-200 font-semibold">{kind === "seller" ? "Sale price" : "Purchase price"}</p>
              <p className="font-display text-[22px] font-bold cf-num">{deal.agreedPrice}</p>
            </div>
          </div>
          {/* Purple progress tracker */}
          <div className="relative mt-6">
            <div className="flex items-center justify-between text-[11px] text-indigo-100/85 mb-2">
              <span className="font-semibold">{STAGE_LABEL[deal.stage]}</span>
              <span className="cf-num font-bold">{deal.progress}% complete</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/15 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-indigo-200 to-white transition-all duration-700" style={{ width: `${deal.progress}%`, boxShadow: "0 0 16px rgba(255,255,255,0.4)" }} />
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-[1100px] mx-auto px-5 md:px-8 py-8 space-y-6">{children}</main>

      {/* Mobile sticky actions */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 px-4 py-3 flex items-center gap-2">
        <button className="flex-1 btn btn-primary !h-11" data-testid={`${kind}-mobile-upload`}><Upload size={13} /> Upload</button>
        <button className="flex-1 btn btn-secondary !h-11" data-testid={`${kind}-mobile-message`}><Sparkles size={13} /> Message agent</button>
      </div>
    </div>
  );
};

const Card = ({ title, eyebrow, children, action, testid }) => (
  <section className="rounded-3xl bg-white border border-slate-200 p-5 md:p-6 cf-shadow-0" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div>
        <p className="cf-eyebrow cf-eyebrow-emerald mb-1">{eyebrow}</p>
        <h2 className="cf-h-section text-[16px]">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

const useDealForPortal = (kind) => {
  const { dealId } = useParams();
  const { deals, findDeal, findProperty, findContact } = useChainStore();
  const deal = dealId ? findDeal(dealId) : deals[0];
  const property = deal ? findProperty(deal.propertyId) : null;
  const agent = deal ? findContact(deal.agentId) : null;
  const partner = deal ? findContact(kind === "seller" ? deal.sellerId : deal.buyerId) : null;
  return { deal, property, agent, partner };
};

export default function SellerPortalPage() {
  const { deal, property, agent, partner } = useDealForPortal("seller");
  const { stages } = useChainStore();
  if (!deal) return null;

  const recentDocs = deal.documents?.slice(0, 5) || [];
  const stageIdx = stages.findIndex((s) => s.id === deal.stage);

  return (
    <PortalLayout kind="seller" deal={deal} property={property} agent={agent} partner={partner} partnerLabel="Seller">
      {/* Property summary */}
      <Card testid="seller-property-card" eyebrow="Your property" title={property?.address?.split(",")[0]}>
        <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-5">
          <div className="aspect-[4/3] rounded-2xl bg-slate-100 overflow-hidden">{property?.thumb && <img src={property.thumb} alt="" className="w-full h-full object-cover" />}</div>
          <div className="space-y-2 text-[13px]">
            <p className="text-slate-700">{property?.address}</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {[{ l: "Type", v: property?.type }, { l: "Bedrooms", v: property?.beds }, { l: "Bathrooms", v: property?.baths }, { l: "EPC", v: property?.epc }].map((x) => (
                <div key={x.l} className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                  <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{x.l}</p>
                  <p className="text-[12.5px] font-bold text-slate-900 cf-num">{x.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Progress timeline */}
      <Card testid="seller-progress-timeline" eyebrow="Your sale" title="Where things stand">
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {stages.map((s, i) => {
            const done = i < stageIdx;
            const active = i === stageIdx;
            return (
              <li key={s.id} className={`rounded-xl p-3 border ${active ? "border-indigo-300 bg-indigo-50" : done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center ${active ? "bg-indigo-600 text-white" : done ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-400 text-[10px] font-bold"}`}>
                    {done ? <CheckCircle2 size={11} /> : i + 1}
                  </span>
                  <p className={`text-[11px] font-bold ${active ? "text-indigo-900" : done ? "text-emerald-900" : "text-slate-700"}`}>{s.label}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* What happens next */}
        <Card testid="seller-next-steps" eyebrow="Plan ahead" title="What happens next?">
          <ol className="space-y-3 text-[12.5px] text-slate-700 list-decimal list-inside">
            <li>Buyer solicitor returns enquiries to your team.</li>
            <li>Mortgage offer issued by lender (target: this week).</li>
            <li>Final contract pack approved by both solicitors.</li>
            <li>Exchange of contracts on <span className="cf-num font-semibold">{deal.targetExchange}</span>.</li>
            <li>Completion booked for <span className="cf-num font-semibold">{deal.targetCompletion}</span>.</li>
          </ol>
        </Card>

        {/* Outstanding actions */}
        <Card testid="seller-outstanding-actions" eyebrow="Action" title="Outstanding for you">
          <ul className="space-y-2">
            {[
              "Approve the latest brochure version",
              "Confirm available viewing slots for Saturday",
              "Sign Memorandum of Sale (e-sign sent)",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-slate-200 bg-slate-50/40 hover:bg-indigo-50 transition-colors">
                <span className="w-4 h-4 rounded-md border-2 border-slate-300 shrink-0" />
                <p className="text-[12.5px] text-slate-800 flex-1">{t}</p>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Documents */}
      <Card testid="seller-documents" eyebrow="Documents" title="Recently uploaded" action={<button className="btn btn-primary !h-9" data-testid="seller-portal-upload"><Upload size={13} /> Upload</button>}>
        {recentDocs.length === 0 ? (
          <p className="text-[12.5px] text-slate-500 text-center py-6">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-1.5">
            {recentDocs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100 text-indigo-700 flex items-center justify-center"><FileText size={14} /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-bold text-slate-900 truncate">{d.name}</p>
                  <p className="text-[10.5px] text-slate-500">{d.tag} · {d.size}</p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.14em] bg-emerald-50 text-emerald-700 border border-emerald-200">Verified</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PortalLayout>
  );
}
